import { useState, useEffect, useRef, useCallback } from 'react';
import api from 'services/api';
import ReactMarkdown from 'react-markdown';

// Rendu markdown — dupliqué du widget ChatbotIA (consigne : pas de module partagé).
const MD_COMPONENTS = {
  h1: ({ children }) => <div style={{ fontSize: 15, fontWeight: 700, color: '#8B0000', margin: '8px 0 4px' }}>{children}</div>,
  h2: ({ children }) => <div style={{ fontSize: 14, fontWeight: 700, color: '#8B0000', margin: '8px 0 4px' }}>{children}</div>,
  h3: ({ children }) => <div style={{ fontSize: 13, fontWeight: 700, color: '#8B0000', margin: '6px 0 3px' }}>{children}</div>,
  strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
  ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 2, lineHeight: 1.5 }}>{children}</li>,
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '8px 0' }} />,
  p: ({ children }) => <p style={{ margin: '4px 0', lineHeight: 1.5 }}>{children}</p>,
  code: ({ children }) => <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>{children}</code>,
};

const SUGGESTIONS = [
  "Combien d'agents actifs ?",
  "Quel est notre chiffre d'affaires ce mois ?",
  "Quels agents ont des contrats qui expirent ?",
  "Quel client est le plus rentable ?",
  "Quelles factures sont impayees ?",
  "Quel site a eu le plus d'incidents ?",
  "Genere le resume mensuel",
];

export default function AssistantPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const chatRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    api.get('/ia/historique?limit=10').then(r => setHistory(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = useCallback(async (text) => {
    const q = text || input.trim();
    if (!q || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: q }]);
    setInput('');
    setLoading(true);

    try {
      const r = await api.post('/ia/chat', { question: q });
      setMessages(prev => [...prev, { role: 'assistant', content: r.data.reponse, model: r.data.model, usage: r.data.usage, fromCache: r.data.fromCache }]);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur de connexion au service IA';
      setMessages(prev => [...prev, { role: 'error', content: msg }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 140px)' }}>
      {/* Chat principal */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div className="content-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div className="gss-toolbar" style={{ borderBottom: '1px solid #eee', padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#8B0000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 700, fontFamily: "'Georgia', 'Times New Roman', serif" }}>IA</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Assistant GSS-360</div>
                {/* Le produit, pas une societe : ce libelle s'affichait chez TOUS les
                    tenants. Meme defaut que le prompt systeme corrige le 27/08. */}
                <div style={{ fontSize: 11, color: '#888' }}>Intelligence IA</div>
              </div>
            </div>
            <button className="gss-btn" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => setShowHistory(!showHistory)}>
              {showHistory ? 'Masquer' : 'Historique'}
            </button>
          </div>

          {/* Messages */}
          <div ref={chatRef} style={{ flex: 1, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#888' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                </div>
                <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Assistant IA</p>
                <p style={{ fontSize: 12, maxWidth: 400, margin: '0 auto 16px' }}>
                  Posez vos questions sur les agents, sites, incidents, finances, clients... L'IA analyse les donnees reelles de votre plateforme.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => sendMessage(s)}
                      style={{ padding: '6px 12px', fontSize: 11, background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 16, cursor: 'pointer', color: '#333' }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', gap: 8 }}>
                {m.role !== 'user' && (
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: m.role === 'error' ? '#DC2626' : '#8B0000', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                    {m.role === 'error' ? '!' : 'G'}
                  </div>
                )}
                <div style={{
                  maxWidth: '80%',
                  padding: '10px 14px',
                  borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: m.role === 'user' ? '#8B1A1A' : m.role === 'error' ? '#FEF2F2' : '#f5f5f5',
                  color: m.role === 'user' ? '#fff' : m.role === 'error' ? '#DC2626' : '#333',
                  fontSize: 13, lineHeight: 1.5,
                }}>
                  {m.role === 'assistant'
                    ? <ReactMarkdown components={MD_COMPONENTS}>{m.content}</ReactMarkdown>
                    : <span style={{ whiteSpace: 'pre-wrap' }}>{m.content}</span>}
                  {m.fromCache && <span style={{ display: 'block', fontSize: 10, color: '#888', marginTop: 4 }}>(cache)</span>}
                  {m.model && <span style={{ display: 'block', fontSize: 10, color: '#aaa', marginTop: 2 }}>{m.model}</span>}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#8B0000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: "'Georgia', 'Times New Roman', serif" }}>G</div>
                <div style={{ padding: '10px 14px', background: '#f5f5f5', borderRadius: '16px 16px 16px 4px', fontSize: 13, color: '#888' }}>
                  Analyse en cours...
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{ borderTop: '1px solid #eee', padding: 12, display: 'flex', gap: 8 }}>
            <textarea ref={inputRef} className="gss-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Posez votre question..." rows={1}
              style={{ flex: 1, resize: 'none', padding: '10px 14px', height: 42 }} />
            <button className="gss-btn gss-btn-primary" onClick={() => sendMessage()} disabled={loading || !input.trim()}
              style={{ height: 42, padding: '0 20px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22,2 15,22 11,13 2,9"/></svg>
              Envoyer
            </button>
          </div>
        </div>
      </div>

      {/* Panneau historique */}
      {showHistory && (
        <div className="content-card" style={{ width: 300, overflow: 'auto', flexShrink: 0 }}>
          <div className="content-card-title" style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>Historique</div>
          {history.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#888', fontSize: 12 }}>Aucun historique</p>
          ) : (
            <div style={{ padding: 8 }}>
              {history.map(h => (
                <div key={h.id} style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', fontSize: 12 }}
                  onClick={() => { setInput(h.question); setShowHistory(false); }}>
                  <div style={{ fontWeight: 600, color: '#333', marginBottom: 2 }}>{h.question?.substring(0, 60)}</div>
                  <div style={{ color: '#888', fontSize: 10 }}>{h.module} - {new Date(h.created_at).toLocaleDateString('fr-FR')}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
